import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiFilm } = FiIcons;

const Hero = () => {
  const navigate = useNavigate();

  const scrollToProjects = () => {
    const element = document.getElementById('showcase');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-black">
      {/* Background Video/Image Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1478720568477-152d9b164e26?auto=format&fit=crop&w=2000&q=80" 
          className="w-full h-full object-cover opacity-40 grayscale"
          alt="Film camera and projector imagery representing cinematic storytelling"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 w-full pt-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-orange text-white text-[10px] font-bold uppercase tracking-[0.3em] mb-8 shadow-lg shadow-accent-orange/20">
              <SafeIcon icon={FiFilm} className="w-3 h-3" />
              <span>Visionary Director</span>
            </div>
            
            <div className="mb-8">
              <h2 className="text-5xl lg:text-7xl font-serif font-bold text-white leading-tight uppercase tracking-tighter whitespace-nowrap">
                Kevin V. Tudor
              </h2>
              <div className="mt-2 text-accent-orange font-bold uppercase tracking-[0.5em] text-[10px] italic flex items-center gap-4">
                <div className="w-8 h-px bg-accent-orange/50"></div>
                Director
              </div>
            </div>

            <p className="text-xl text-white/60 mb-10 leading-relaxed max-w-xl font-medium font-serif italic">
              "Capturing the raw essence of human experience through the lens of cinematic storytelling."
            </p>
            
            <div className="flex flex-wrap gap-5">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/shorts')}
                className="bg-white text-black px-10 py-5 rounded-full font-bold flex items-center gap-3 shadow-2xl hover:bg-accent-orange hover:text-white transition-all duration-500 uppercase tracking-widest text-xs"
              >
                Watch Reels
                <SafeIcon icon={FiPlay} className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToProjects}
                className="bg-transparent border-2 border-white/20 text-white px-10 py-5 rounded-full font-bold hover:border-accent-orange transition-all duration-500 uppercase tracking-widest text-xs"
              >
                View Projects
              </motion.button>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            className="hidden lg:block relative"
          >
            <div className="relative group">
              <div className="absolute -inset-4 bg-accent-orange/10 rounded-[48px] blur-2xl opacity-0 group-hover:opacity-100 transition duration-1000"></div>
              <div className="absolute -inset-1 bg-gradient-to-r from-accent-orange to-black rounded-[40px] blur opacity-25 group-hover:opacity-40 transition duration-1000"></div>
              
              <div className="relative aspect-[3/4] rounded-[40px] overflow-hidden border border-white/10 bg-zinc-900 shadow-2xl">
                <img
                  src="https://s3.amazonaws.com/mavstorage/699633ea73b97_tinman-doc-son.jpeg?"
                  alt="Kevin V. Tudor directing a cinematic production on set"
                  className="w-full h-full object-cover grayscale transition-all duration-1000 group-hover:scale-105 group-hover:grayscale-0"
                  fetchpriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60"></div>
              </div>

              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-accent-orange/10 rounded-full blur-3xl"></div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 text-white/30">
        <span className="text-[10px] font-bold uppercase tracking-[0.5em] animate-pulse">Scroll to Explore</span>
        <div className="w-px h-12 bg-gradient-to-b from-accent-orange to-transparent" />
      </div>
    </section>
  );
};

export default Hero;